"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VermontRules = exports.VT_THRESHOLDS = void 0;
const browser_1 = require("encamp-prisma/generated/browser");
const _FEDERAL_1 = require("./_FEDERAL");
const _stateRules_1 = require("./_stateRules");
/**
 * Vermont (ANR DEC) accumulation limits, Vermont Hazardous Waste Management
 * Regulations (VHWMR) §§ 7-306 to 7-311, effective July 10, 2026.
 *
 * Vermont writes its generator standards out in full. Two departures reach a
 * container's limits: no SQG 270-day long-haul option (§ 7-307(c)(2) is 180
 * days flat), and a one-cubic-yard satellite allowance for non-liquid
 * Vermont-listed waste (§ 7-310(a)). Vermont calls the central area a
 * "short-term storage area".
 *
 * Vermont's generator tiers are the federal ones, and RCRAInfo offers it only
 * "same as federal", so this class keys on the federal status alone.
 *
 * ── KNOWN GAPS
 *
 * 1. The one-cubic-yard allowance and the full-container trigger.
 * 2. Shift accumulation (§ 7-310(b)) — an elective alternative; not modeled.
 *    Over-strict: its accumulation containers sit in the short-term storage
 *    area undated until they reach the caps or fill, but the central clock
 *    here runs from first fill.
 * 3. LQG F006 180-day limit (§ 7-308(b)(2)) — not modeled, same as federal.
 * 4. The ban on transfers between satellite containers — DEC guidance (the
 *    2023 "Accumulation and Storage" fact sheet), not VHWMR text; not a
 *    limit, so not modeled.
 * 5. Which program governs a container is answered the federal way; Vermont's
 *    universal waste (subchapter 9), used oil and pharmaceutical provisions
 *    have not been read to confirm it.
 */
/** Vermont (ANR DEC) accumulation limits, VHWMR §§ 7-306 to 7-311. */
const VT_SATELLITE_SOURCE = { jurisdiction: "VT", citation: "VHWMR § 7-310(a)" };
const VT_VSQG_SOURCE = { jurisdiction: "VT", citation: "VHWMR § 7-306" };
/** Federal rules as Vermont writes them. */
exports.VT_THRESHOLDS = {
    centralTimeLimit: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.centralTimeLimit,
        source: { jurisdiction: "VT", citation: "VHWMR §§ 7-307(c)(2), 7-308(b)(2)" },
    },
    capNonAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capNonAcuteVolume, source: VT_SATELLITE_SOURCE },
    capAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteVolume, source: VT_SATELLITE_SOURCE },
    capAcuteMass: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteMass, source: VT_SATELLITE_SOURCE },
    satelliteToCentralMoveWindow: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.satelliteToCentralMoveWindow,
        source: VT_SATELLITE_SOURCE,
    },
};
class VermontRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.stateStatusDomain = [];
        this.adoptsEpisodicEvents = true;
    }
    facilityThresholds(facility) {
        return _FEDERAL_1.Federal.onSiteThresholds(facility);
    }
    accumulationCategory(container) {
        // STATE_REGULATED is non-acute pending the waste team's per-state decision.
        return container.classification === browser_1.WasteClassification.STATE_REGULATED
            ? { value: "NON_ACUTE" }
            : _FEDERAL_1.Federal.category(container);
    }
    governingProgram(container) {
        return _FEDERAL_1.Federal.governingProgram(container);
    }
    /** No 270-day long-haul option, so `mustTransport200Miles` is not read. */
    centralTimeLimit(situation) {
        const status = situation.facility.federalGeneratorStatus;
        switch (status) {
            case undefined:
                // Federal law names the same missing status.
                return _FEDERAL_1.Federal.centralTimeLimit(situation);
            case "VSQG":
                return {
                    ...exports.VT_THRESHOLDS.centralTimeLimit,
                    source: VT_VSQG_SOURCE,
                    determination: "EXEMPT",
                    basis: { facility: { federalGeneratorStatus: status } },
                };
            case "LQG":
            case "SQG": {
                const source = {
                    jurisdiction: "VT",
                    citation: status === "LQG" ? "VHWMR § 7-308(b)(2)" : "VHWMR § 7-307(c)(2)",
                };
                return {
                    ...exports.VT_THRESHOLDS.centralTimeLimit,
                    source,
                    determination: "KNOWN",
                    basis: { facility: { federalGeneratorStatus: status } },
                    value: status === "LQG" ? 90 : 180,
                };
            }
        }
    }
    containerThresholds(situation) {
        const program = _FEDERAL_1.Federal.governingProgram(situation.container);
        const episodic = _FEDERAL_1.Federal.episodicWindow(situation.container, program);
        // A satellite container's move window reports a missing program itself.
        if (program.value === undefined && situation.area.type !== "SATELLITE") {
            const missing = [
                ...program.missing,
                ...(situation.area.type === undefined ? ["area.type"] : []),
            ];
            return [
                { ...exports.VT_THRESHOLDS.centralTimeLimit, determination: "UNDETERMINED", missing, basis: {} },
                ...episodic,
            ];
        }
        if (program.value !== undefined && _FEDERAL_1.Federal.isSubclassification(program.value)) {
            return [_FEDERAL_1.Federal.subclassificationTimeLimits[program.value], ...episodic];
        }
        switch (situation.area.type) {
            case "CENTRAL":
                return [this.centralTimeLimit(situation), ...episodic];
            case "SATELLITE": {
                const caps = this.satelliteCaps(situation);
                return [
                    ..._FEDERAL_1.Federal.satelliteMoveWindow(this, exports.VT_THRESHOLDS.satelliteToCentralMoveWindow, caps, situation.container),
                    ...episodic,
                ];
            }
            case undefined:
                return [
                    {
                        ...exports.VT_THRESHOLDS.centralTimeLimit,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    ...episodic,
                ];
        }
    }
    /**
     * The caps, which apply simultaneously; crossing one starts its move
     * window. An unknown status still gets them, the more protective default.
     */
    satelliteCaps(situation) {
        const status = situation.facility.federalGeneratorStatus;
        const basis = status === undefined ? {} : { facility: { federalGeneratorStatus: status } };
        if (status === "VSQG") {
            return [
                {
                    ...exports.VT_THRESHOLDS.capNonAcuteVolume,
                    source: VT_VSQG_SOURCE,
                    determination: "EXEMPT",
                    basis,
                },
                {
                    ...exports.VT_THRESHOLDS.capAcuteVolume,
                    source: VT_VSQG_SOURCE,
                    determination: "EXEMPT",
                    basis,
                },
                {
                    ...exports.VT_THRESHOLDS.capAcuteMass,
                    source: VT_VSQG_SOURCE,
                    determination: "EXEMPT",
                    basis,
                },
            ];
        }
        return [
            {
                ...exports.VT_THRESHOLDS.capNonAcuteVolume,
                determination: "KNOWN",
                basis,
            },
            {
                ...exports.VT_THRESHOLDS.capAcuteVolume,
                determination: "KNOWN",
                basis,
            },
            {
                ...exports.VT_THRESHOLDS.capAcuteMass,
                determination: "KNOWN",
                basis,
            },
        ];
    }
    areaThresholds(situation) {
        switch (situation.area.type) {
            case "CENTRAL":
                return [];
            case "SATELLITE":
                return this.satelliteCaps(situation);
            case undefined:
                return [
                    {
                        ...exports.VT_THRESHOLDS.capNonAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.VT_THRESHOLDS.capAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.VT_THRESHOLDS.capAcuteMass,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
}
exports.VermontRules = VermontRules;
//# sourceMappingURL=VT.js.map